import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * CupAdminPanel — pannello admin per comporre e gestire una coppa.
 * - Composizione partecipanti (european: scegli squadre; national: tutte della
 *   nazione + 2 teste di serie; supercup: 2 finaliste)
 * - Genera coppa (primo turno)
 * - Avanza turno (quando il turno corrente è completo)
 */
export default function CupAdminPanel({ cup, onChanged }) {
    const [teams, setTeams] = useState([]);
    const [selected, setSelected] = useState(new Set(cup.participants || []));
    const [seeded, setSeeded] = useState(new Set(cup.seeded_team_ids || []));
    const [finalists, setFinalists] = useState(cup.finalists || []);
    const [query, setQuery] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const kind = cup.kind; // european | national | supercup

    useEffect(() => {
        api.get("/teams").then(({ data }) => {
            let list = data || [];
            if (kind === "national") list = list.filter((t) => t.nation === cup.nation);
            list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setTeams(list);
            // national: partecipanti = tutte le squadre della nazione di default
            if (kind === "national" && (cup.participants || []).length === 0) {
                setSelected(new Set(list.map((t) => t.id)));
            }
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const flash = (m, isErr = false) => {
        if (isErr) { setErr(m); setMsg(""); } else { setMsg(m); setErr(""); }
        setTimeout(() => { setMsg(""); setErr(""); }, 5000);
    };

    const toggle = (setFn, set, id, max = null) => {
        const next = new Set(set);
        if (next.has(id)) next.delete(id);
        else {
            if (max && next.size >= max) return;
            next.add(id);
        }
        setFn(next);
    };

    const toggleFinalist = (id) => {
        setFinalists((prev) => {
            if (prev.includes(id)) return prev.filter((x) => x !== id);
            if (prev.length >= 2) return prev;
            return [...prev, id];
        });
    };

    const saveParticipants = async () => {
        setBusy(true);
        try {
            const body = {
                team_ids: kind === "supercup" ? finalists : Array.from(selected),
                seeded_team_ids: kind === "national" ? Array.from(seeded) : [],
                finalists: kind === "supercup" ? finalists : [],
                auto_nation: kind === "national",
            };
            await api.put(`/cups/${cup.slug}/participants`, body);
            flash("Partecipanti salvati.");
            onChanged?.();
        } catch (e) {
            flash(e.response?.data?.detail || "Errore", true);
        } finally { setBusy(false); }
    };

    const generate = async () => {
        if (!confirm("Generare il primo turno? Eventuali partite esistenti verranno sostituite.")) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/cups/${cup.slug}/generate`);
            flash(`Coppa generata. Turno attuale: ${data.phase}.`);
            onChanged?.();
        } catch (e) {
            flash(e.response?.data?.detail || "Errore", true);
        } finally { setBusy(false); }
    };

    const advance = async () => {
        if (!confirm("Avanzare al turno successivo? Le qualificate verranno calcolate dai risultati.")) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/cups/${cup.slug}/advance`);
            flash(data.status === "completed" ? "Coppa completata! Vincitrice assegnata." : `Turno avanzato: ${data.phase}.`);
            onChanged?.();
        } catch (e) {
            flash(e.response?.data?.detail || "Errore", true);
        } finally { setBusy(false); }
    };

    const resetCup = async () => {
        if (!confirm("Annullare la coppa e cancellare TUTTE le partite generate? I partecipanti restano: potrai ri-cliccare 'Genera coppa' per un nuovo sorteggio con le date corrette.")) return;
        setBusy(true);
        try {
            await api.post(`/cups/${cup.slug}/reset`);
            flash("Coppa annullata. Ora puoi rigenerare il sorteggio.");
            onChanged?.();
        } catch (e) {
            flash(e.response?.data?.detail || "Errore", true);
        } finally { setBusy(false); }
    };

    const filtered = teams.filter((t) => t.name?.toLowerCase().includes(query.toLowerCase()));

    return (
        <div className="op-card p-4 mt-6 border border-tff7a1a/40" data-testid="cup-admin-panel" style={{ background: "rgba(255,122,26,0.04)" }}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="op-label text-tff7a1a">// GESTIONE COPPA (ADMIN)</div>
                <div className="op-mono text-[10px] text-t8f95a3">
                    Fase attuale: <span className="text-tffffff">{cup.phase || "non generata"}</span>
                    {cup.status === "completed" && <span className="text-t7ce38b"> · COMPLETATA</span>}
                </div>
            </div>

            {/* Composizione */}
            {kind === "supercup" ? (
                <div>
                    <div className="op-label text-[10px] mb-2">Scegli le 2 finaliste ({finalists.length}/2)</div>
                    <input
                        value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca squadra…"
                        className="bg-t0d0f12 border border-t22252a px-3 py-1.5 text-tffffff text-sm mb-2 w-full max-w-xs focus:border-t2bb6ff focus:outline-none"
                        data-testid="cup-team-search"
                    />
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 max-h-56 overflow-y-auto">
                        {filtered.map((t) => (
                            <button
                                key={t.id} onClick={() => toggleFinalist(t.id)}
                                className={`flex items-center gap-2 p-2 border text-left text-xs transition-colors ${finalists.includes(t.id) ? "border-tff7a1a bg-tff7a1a/15 text-tffffff" : "border-t22252a text-tc9ccd3 hover:border-tff7a1a/50"}`}
                                data-testid={`cup-finalist-${t.id}`}
                            >
                                {t.logo_url && <img src={t.logo_url} alt="" className="w-4 h-4 object-contain" />}
                                <span className="truncate">{t.name}</span>
                            </button>
                        ))}
                    </div>
                </div>
            ) : (
                <div>
                    <div className="op-label text-[10px] mb-2">
                        {kind === "national"
                            ? `Partecipanti: tutte le squadre ${cup.nation} · seleziona le 2 TESTE DI SERIE (${seeded.size}/2) che saltano ai Quarti`
                            : `Seleziona le squadre partecipanti (${selected.size} selezionate · serve numero PARI ≥ 10)`}
                    </div>
                    <input
                        value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca squadra…"
                        className="bg-t0d0f12 border border-t22252a px-3 py-1.5 text-tffffff text-sm mb-2 w-full max-w-xs focus:border-t2bb6ff focus:outline-none"
                        data-testid="cup-team-search"
                    />
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 max-h-72 overflow-y-auto">
                        {filtered.map((t) => {
                            const isSel = selected.has(t.id);
                            const isSeed = seeded.has(t.id);
                            return (
                                <div key={t.id} className={`flex items-center gap-2 p-2 border text-xs ${isSel ? "border-t2bb6ff/60 bg-t2bb6ff/10" : "border-t22252a"}`}>
                                    {kind === "european" && (
                                        <button onClick={() => toggle(setSelected, selected, t.id)} data-testid={`cup-participant-${t.id}`} className="shrink-0">
                                            <span className={`inline-block w-4 h-4 border ${isSel ? "bg-t2bb6ff border-t2bb6ff" : "border-t5c6370"}`} />
                                        </button>
                                    )}
                                    {t.logo_url && <img src={t.logo_url} alt="" className="w-4 h-4 object-contain" />}
                                    <span className="truncate flex-1 text-tc9ccd3">{t.name}</span>
                                    {kind === "national" && (
                                        <button
                                            onClick={() => toggle(setSeeded, seeded, t.id, 2)}
                                            className={`op-label text-[9px] px-1.5 py-0.5 border ${isSeed ? "border-tffd700 text-tffd700 bg-tffd700/10" : "border-t5c6370 text-t5c6370"}`}
                                            data-testid={`cup-seed-${t.id}`}
                                            title="Testa di serie (salta ai Quarti)"
                                        >
                                            ★
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={saveParticipants} disabled={busy} className="op-btn disabled:opacity-50" data-testid="cup-save-participants">
                    {busy ? "…" : "Salva partecipanti"}
                </button>
                <button onClick={generate} disabled={busy} className="op-btn op-btn-primary disabled:opacity-50" data-testid="cup-generate-btn">
                    {busy ? "…" : "Genera coppa"}
                </button>
                <button onClick={advance} disabled={busy || !cup.phase || cup.status === "completed"} className="op-btn disabled:opacity-50" data-testid="cup-advance-btn">
                    {busy ? "…" : "Avanza turno →"}
                </button>
                {cup.phase && (
                    <button onClick={resetCup} disabled={busy} className="op-btn disabled:opacity-50 border-tff4d4d text-tff4d4d hover:bg-tff4d4d/10" data-testid="cup-reset-btn">
                        {busy ? "…" : "↺ Annulla coppa e rigenera"}
                    </button>
                )}
            </div>
            {msg && <div className="op-mono text-xs text-t7ce38b mt-2" data-testid="cup-admin-msg">{msg}</div>}
            {err && <div className="op-mono text-xs text-tff4d4d mt-2" data-testid="cup-admin-err">{err}</div>}
        </div>
    );
}
